<script lang='ts'>
  interface Props {
    dashboardState?: any
  }

  let {
    dashboardState,
  }: Props = $props()

  // Extract overview data from dashboardState
  let overview = $derived(dashboardState?.overview || {
    pages: 0,
    PDFs: 0,
    images: 0,
    brokenLinks: 0,
  })

  let hoveredItem = $state<string | null>(null)
  let focusedItem = $state<string | null>(null)
  
  // Show tooltip if either hovered or focused
  let activeTooltip = $derived(hoveredItem || focusedItem)

  // Animated display values
  let displayed = $state({ pages: 0, PDFs: 0, images: 0, brokenLinks: 0 })

  function animateCount(target: number, key: keyof typeof displayed, duration = 900) {
    const start = performance.now()
    function step(now: number) {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      displayed[key] = Math.round(eased * target)
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }

  $effect(() => {
    const { pages, PDFs, images, brokenLinks } = overview
    if (pages || PDFs || images || brokenLinks) {
      animateCount(pages, 'pages')
      animateCount(PDFs, 'PDFs')
      animateCount(images, 'images')
      animateCount(brokenLinks, 'brokenLinks')
    }
  })
</script>

{#snippet item(icon, title, value, tooltip, id, color)}
  <div class="web-overview-item" role="tooltip"  tabindex="-1" onmouseenter={() => hoveredItem = id} onmouseleave={() => hoveredItem = null} onfocus={() => focusedItem = id} onblur={() => focusedItem = null}>
    <span class="icon" style="color: {color}">{@html icon}</span>
    <span class="title">{title}</span>
    <span class="value">{value}</span>
    {#if activeTooltip === id}
      <div class="tooltip-bubble">
        {tooltip}
      </div>
    {/if}
  </div>
{/snippet}

<div class="web-overview">
  {@render item('<i class="fa-solid fa-page"></i>',      'Pages',        displayed.pages,       'Total number of web pages', 'pages',       '#FFA44B')}
  {@render item('<i class="fa-duotone fa-solid fa-file-pdf"></i>',  'PDF\'s',         displayed.PDFs,        'Total number of PDF docs',  'pdfs',        'var(--uc-red)')}
  {@render item('<i class="fa-duotone fa-solid fa-image"></i>',     'Images',       displayed.images,      'Total number of images',    'images',      '#2c3d80')}
  {@render item('<i class="fa-duotone fa-solid fa-link-slash"></i>','Broken Links', displayed.brokenLinks, 'Number of broken links',    'broken-links','#618764')}
</div>

<style lang="scss">
  .web-overview {
    margin: 0 0 3rem 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    gap: 1rem;

    .web-overview-item {
      flex: 1 1 15rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      position: relative;
      cursor: pointer;
      border-radius: 8px;
      padding: 1rem;
      transition: background-color 0.2s ease, transform 0.2s ease;

      &:hover,
      &:focus {
        background-color: rgba(0, 0, 0, 0.05);
        transform: translateY(-2px);
        outline: none;
      }

      &:focus {
        box-shadow: 0 0 0 3px rgba(var(--uc-red-rgb, 220, 38, 127), 0.3);
      }

      span.icon {
        font-size: 4rem;
        margin-bottom: 1rem;
      }

      span.title {
        font-size: 2rem;
        font-weight: bold;
        padding: 0rem, 1rem;
        margin-bottom: 1rem;
        text-transform: uppercase;
      }
      
      span.value {
        font-size: 4rem;
        font-weight: bold;
        color: var(--uc-red);
      }

      .tooltip-bubble {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background-color: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 1.5rem;
        white-space: nowrap;
        z-index: 1000;
        margin-bottom: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        pointer-events: none;

        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: rgba(0, 0, 0, 0.9);
        }
      }
    }
  }
</style>